import { AnyID, Item, LinkedItem, Partition, Store, Walls } from '@types'
import { toVector3 } from '@utils/conversion/toVector3'
import { degrees } from '@utils/generalMaths/geometry'
import { Euler, Matrix3, Matrix4, Quaternion, Vector3 } from 'three'
import { OBB } from 'three/examples/jsm/math/OBB'
import {
  OPPOSITE_SIDE,
  getObbCenter,
  getSideFacing,
  getTouchedFace,
  releaseSnappedPair,
  releaseSnappedSide,
  releaseSnappedSlabs,
  setSnappedSide,
} from './snappedSides'

/** Ancho efectivo del item respetando el morph de ancho (misma lógica que collideItems) */
export function getMorphedItemWidth(item: Item | LinkedItem): number {
  const widthMorph = item.morphs?.find((morph) => morph.name === 'Ancho' || morph.name === 'width')
  return item.long?.toLowerCase().includes('encimera b3200')
    ? (widthMorph?._default ?? item.width ?? 0)
    : item.long !== 'Frontal' || item.long?.toLowerCase().includes('zocalo')
      ? (widthMorph?.default ?? item.width ?? 0)
      : (widthMorph?._default ?? item.width ?? 0)
}

/**
 * Snap por caras tipo pouff (misma lógica que collideItems/collidePouffItems) de un item
 * contra una "losa" (pared o partición) tratada como OBB: el item se pega a la primera
 * cara que encuentra su centro (frontal/trasera/extremos izq-der), FORZANDO su rotación
 * para que su cara trasera quede pegada y paralela a esa cara (ignorando la orientación
 * que traía al arrastrarlo). El centro del OBB se recalcula preservando su posición a lo
 * largo de la losa y ajustando solo la proyección normal para quedar a tope; luego se
 * vuelve a bajar al origen esquina-inferior-trasera-izquierda con la rotación forzada.
 * Registra snappedWall, highItems/lowItems, snappedSides (con vecinos flush en caras
 * largas), sincroniza item.rotation y actualiza la OBB del item en sceneBoundingBoxes.
 *
 * `slabCenter` es el centro del OBB de la losa en el plano XZ y `slabQuat` su rotación
 * (X local a lo largo de la losa, Z local a través del grosor).
 */
export function projectItemOnSlabFaces(
  store: Store,
  itemID: AnyID,
  slabID: AnyID,
  slab: Walls | Partition,
  itemMatrix: Matrix4,
  slabQuat: Quaternion,
  slabCenter: Vector3,
  slabHalfWidth: number,
  preserveY: boolean,
) {
  const items = store.items
  const item = items.get(itemID) as Item | LinkedItem
  if (!item) return
  const itemWidth = getMorphedItemWidth(item)

  const itemPosition = new Vector3()
  const itemQuat = new Quaternion()
  itemMatrix.decompose(itemPosition, itemQuat, new Vector3())

  const slabXAxis = new Vector3(1, 0, 0).applyQuaternion(slabQuat)
  const slabZAxis = new Vector3(0, 0, 1).applyQuaternion(slabQuat)
  const slabHalfThickness = (slab.thickness || 0) / 2

  // La cara se elige con el CENTRO del OBB del item, no con la esquina: las
  // colocaciones programáticas (laterales, getRelativeMatrix) dejan la esquina
  // exactamente sobre la cara y su signo sería ruido de coma flotante.
  const itemCenterCurrent = getObbCenter(
    itemPosition,
    itemQuat,
    itemWidth,
    item.height || 0,
    item.thickness || 0,
  )
  // Los extremos izq/der de una pared son esquinas cuando conectan con otra
  // pared (prevWall/nextWall): ahí no hay cara real donde apoyar el item, así
  // que se bloquean para forzar el apoyo en la cara frontal/trasera incluso
  // cerca de la esquina, y poder "saltar" a la pared contigua al arrastrar.
  const blockedXSides = { left: !!slab.prevWall, right: !!slab.nextWall }
  // Las paredes de perímetro solo tienen un lado habitable: la cara frontal
  // (la que mira a la habitación). Las particiones (tabiques), al estar
  // exentas, sí pueden recibir items por cualquiera de sus caras.
  const frontOnly = slab.type === 'walls'
  const touched = getTouchedFace(
    slabQuat,
    slabCenter,
    slabHalfWidth,
    slabHalfThickness,
    itemCenterCurrent,
    blockedXSides,
    frontOnly,
  )
  const faceNormal = touched.axis.clone().multiplyScalar(touched.sign)

  // Forzar la rotación del item para que su cara trasera (Z local = 0) quede
  // pegada y paralela a la cara tocada: el eje Z local (frente) pasa a apuntar
  // hacia faceNormal (fuera de la losa, hacia la habitación), sin importar la
  // rotación que traía el item al arrastrarlo.
  const newItemQuat = new Quaternion().setFromUnitVectors(new Vector3(0, 0, 1), faceNormal)
  const itemXAxis = new Vector3(1, 0, 0).applyQuaternion(newItemQuat)
  const itemZAxis = new Vector3(0, 0, 1).applyQuaternion(newItemQuat)
  const itemExtent =
    (itemWidth / 2) * Math.abs(itemXAxis.dot(faceNormal)) +
    ((item.thickness || 0) / 2) * Math.abs(itemZAxis.dot(faceNormal))
  // Semi-dimensión del item proyectada sobre el eje largo de la losa (X local):
  // limita cuánto puede acercarse su centro al principio/final de la pared.
  const halfAlong =
    (itemWidth / 2) * Math.abs(itemXAxis.dot(slabXAxis)) +
    ((item.thickness || 0) / 2) * Math.abs(itemZAxis.dot(slabXAxis))

  // Al forzar la rotación, la relación entre la esquina (origen de la matriz)
  // y el centro del OBB cambia respecto al arrastre: ya no basta con desplazar
  // la esquina vieja a lo largo de la normal. Se reconstruye el CENTRO nuevo
  // preservando su posición a lo largo de la losa (eje tangencial a la cara
  // tocada) y fijando la proyección normal para quedar a tope; luego se
  // vuelve a bajar al origen esquina-inferior-trasera-izquierda con la
  // rotación ya forzada.
  const tangentialAxis = touched.onXFace ? slabZAxis : slabXAxis
  const centerDelta = new Vector3(
    itemCenterCurrent.x - slabCenter.x,
    0,
    itemCenterCurrent.z - slabCenter.z,
  )
  let tangentialProjection = centerDelta.dot(tangentialAxis)
  // Deslizando por la cara frontal/trasera: no dejar que el item sobresalga del
  // principio o el final de la pared, se detiene justo en el borde (el salto a
  // la pared contigua en la esquina lo gestiona la detección de esa otra pared).
  if (!touched.onXFace) {
    const maxAlong = Math.max(0, slabHalfWidth - halfAlong)
    tangentialProjection = Math.min(maxAlong, Math.max(-maxAlong, tangentialProjection))
  }
  const desiredProjection = touched.sign * (touched.extent + itemExtent)
  const newCenterXZ = new Vector3(slabCenter.x, 0, slabCenter.z)
    .addScaledVector(tangentialAxis, tangentialProjection)
    .addScaledVector(touched.axis, desiredProjection)
  const newPosition = newCenterXZ
    .addScaledVector(itemXAxis, -itemWidth / 2)
    .addScaledVector(itemZAxis, -(item.thickness || 0) / 2)
  newPosition.y = itemPosition.y

  // Altura: preservar la Y del arrastre; en 3D restringir a [0, altura losa − altura item]
  let finalY = itemPosition.y
  if (!preserveY && store.mode === 'MODE_3D_VIEW') {
    finalY = Math.max(0, finalY)
    if (item.height) {
      finalY = Math.min(finalY, slab.height - item.height)
    }
  }

  item.matrix.makeRotationFromQuaternion(newItemQuat)
  if (!item.visible) item.visible = true
  item.matrix.setPosition(newPosition.x, finalY, newPosition.z)
  item.x = newPosition.x
  item.z = newPosition.z
  item.y = finalY
  // Mantener item.rotation (grados, eje Y) sincronizado con la rotación forzada:
  // otros cálculos (orden de muebles contiguos, dimensiones, OBBs derivadas) leen
  // este campo en vez de descomponer la matriz.
  item.rotation = degrees(new Euler().setFromQuaternion(newItemQuat).y)

  const snappedCenter = getObbCenter(
    new Vector3(newPosition.x, finalY, newPosition.z),
    newItemQuat,
    itemWidth,
    item.height || 0,
    item.thickness || 0,
  )

  // ItemGroups no deben tener snappedWall ni agregarse a las losas
  if (item.type !== 'itemGroup') {
    item.snappedWall = slabID
  }
  if (item.tipoNormalizado === 'MAL') {
    slab.highItems.add(itemID)
  }
  if (item.tipoNormalizado === 'MBJ' || item.tipoNormalizado === 'COL') {
    slab.lowItems.add(itemID)
  }

  // Registrar caras ocupadas: la cara del item que mira a la losa queda ocupada por
  // ella, y los vecinos flush en la misma cara se registran por el lado que se tocan
  // (base de los grupos contiguos por snappedSides). Los extremos de la losa
  // (left/right) no generan vecinos: solo aplican a las caras largas.
  if (item.type !== 'itemGroup') {
    const slabSide = getSideFacing(
      newItemQuat,
      snappedCenter,
      snappedCenter.clone().sub(faceNormal),
    )
    // Solo se libera la cara que va a ocupar la losa (y cualquier losa que quedara en otra
    // cara, porque la rotación forzada puede cambiar cuál mira a la pared). Los vecinos que
    // el item ya tenía registrados se conservan: la proyección no lo despega de ellos, solo
    // corrige la normal, y son los que mantienen viva la fila. Tirarlos todos rompía las L
    // a través de un rinconero, donde los dos tramos viven en paredes distintas y el enlace
    // no se puede reconstruir recorriendo la losa.
    releaseSnappedSide(items, itemID, slabSide)
    releaseSnappedSlabs(items, itemID)
    setSnappedSide(item, slabSide, slabID)
    if (!touched.onXFace) {
      const SIDE_EPS_MM = 1
      // Soltar pide MUCHA más holgura que pegar: si se usara el mismo milímetro, un
      // vecino que collideItems dejó a 2mm (juegos de regleta, redondeos) se soltaría solo
      // en el siguiente frame del arrastre y la fila parpadearía. 30mm es el mismo umbral
      // de contacto que usa rebuildSnappedSides, así que lo que se suelta aquí tampoco lo
      // vuelve a registrar la hidratación.
      const SIDE_RELEASE_EPS_MM = 30
      const myAlong = new Vector3(
        snappedCenter.x - slabCenter.x,
        0,
        snappedCenter.z - slabCenter.z,
      ).dot(slabXAxis)
      const myMin = myAlong - halfAlong
      const myMax = myAlong + halfAlong
      // Lado del item que mira hacia el extremo bajo/alto de la losa, en su
      // propio sistema local (ya forzado a quedar paralelo a la losa)
      const lowSide = getSideFacing(
        newItemQuat,
        snappedCenter,
        snappedCenter.clone().sub(slabXAxis),
      )
      const highSide = OPPOSITE_SIDE[lowSide]
      const slabItems = new Set([...slab.highItems, ...slab.lowItems])
      slabItems.delete(itemID)
      for (const otherItemID of slabItems) {
        const otherItem = items.get(otherItemID as AnyID) as Item | LinkedItem
        if (!otherItem || otherItem.snappedWall !== slabID) continue
        // Solo vecinos al mismo nivel horizontal
        if (
          item.tipoNormalizado === 'MAL' &&
          otherItem.tipoNormalizado !== 'MAL' &&
          otherItem.tipoNormalizado !== 'COL'
        ) {
          continue
        }
        if (
          (item.tipoNormalizado === 'MBJ' || item.tipoNormalizado === 'COL') &&
          otherItem.tipoNormalizado !== 'MBJ' &&
          otherItem.tipoNormalizado !== 'COL'
        ) {
          continue
        }
        const otherItemPos = new Vector3()
        const otherItemQuat = new Quaternion()
        otherItem.matrix.decompose(otherItemPos, otherItemQuat, new Vector3())
        const otherItemWidth = getMorphedItemWidth(otherItem)
        const otherCenter = getObbCenter(
          otherItemPos,
          otherItemQuat,
          otherItemWidth,
          otherItem.height || 0,
          otherItem.thickness || 0,
        )
        // Solo vecinos apoyados en la misma cara de la losa (mismo lado del plano)
        const otherFaceSign =
          slabZAxis.dot(
            new Vector3(otherCenter.x - slabCenter.x, 0, otherCenter.z - slabCenter.z),
          ) < 0
            ? -1
            : 1
        if (otherFaceSign !== touched.sign) continue
        const otherXAxis = new Vector3(1, 0, 0).applyQuaternion(otherItemQuat)
        const otherZAxis = new Vector3(0, 0, 1).applyQuaternion(otherItemQuat)
        const otherHalfAlong =
          (otherItemWidth / 2) * Math.abs(otherXAxis.dot(slabXAxis)) +
          ((otherItem.thickness || 0) / 2) * Math.abs(otherZAxis.dot(slabXAxis))
        const otherAlong = new Vector3(
          otherCenter.x - slabCenter.x,
          0,
          otherCenter.z - slabCenter.z,
        ).dot(slabXAxis)
        const otherMin = otherAlong - otherHalfAlong
        const otherMax = otherAlong + otherHalfAlong
        const otherLowSide = getSideFacing(
          otherItemQuat,
          otherCenter,
          otherCenter.clone().sub(slabXAxis),
        )
        const otherHighSide = OPPOSITE_SIDE[otherLowSide]
        if (Math.abs(otherMax - myMin) <= SIDE_EPS_MM) {
          setSnappedSide(item, lowSide, otherItemID as AnyID)
          setSnappedSide(otherItem, otherHighSide, itemID)
        } else if (Math.abs(myMax - otherMin) <= SIDE_EPS_MM) {
          setSnappedSide(item, highSide, otherItemID as AnyID)
          setSnappedSide(otherItem, otherLowSide, itemID)
        } else if (Math.max(myMin - otherMax, otherMin - myMax) > SIDE_RELEASE_EPS_MM) {
          // Separados a lo largo de la losa: si seguían registrados el uno con el otro es
          // que este arrastre los ha despegado. Solo se deshace ESE par (uno pierde su
          // right y el otro su left); la pared de la espalda y el vecino del otro extremo
          // siguen puestos. Sin esto, arrastrar un mueble lejos de su vecino sin salir de
          // la pared dejaba la fila pegada en el registro: los grupos seguían saliendo
          // unidos y laterales y frontales se dibujaban como si no hubiera hueco.
          //
          // El giro en L de un rinconero no puede caer aquí, y por eso no hay que
          // protegerlo: el mueble que continúa por su cara frontal queda metido en la
          // habitación, no a ras de esta losa, y un rinconero nunca transmite su pared
          // (ver collideItems), así que ese vecino no pasa el filtro de snappedWall de
          // arriba. Lo mismo con el otro brazo de la L, que vive en la pared perpendicular.
          releaseSnappedPair(items, itemID, otherItemID as AnyID)
        }
      }
    }
  }

  // OBB del item en su nueva posición (con la rotación forzada contra la losa)
  const itemCenter = getObbCenter(
    new Vector3(newPosition.x, finalY, newPosition.z),
    newItemQuat,
    item.width,
    item.height,
    item.thickness,
  )
  const rotationMatrix4 = new Matrix4().makeRotationFromQuaternion(newItemQuat)
  const rotationMatrix = new Matrix3().setFromMatrix4(rotationMatrix4)
  store.sceneBoundingBoxes.set(itemID, {
    boundingBox: new OBB(
      itemCenter,
      toVector3([item.width / 2, item.height / 2, item.thickness / 2]),
      rotationMatrix,
    ),
    type: 'items',
  })
}
